import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import { YomiriLogo } from './YomiriLogo.tsx';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import MoreVert from '@mui/icons-material/MoreVert';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import Refresh from '@mui/icons-material/Refresh';
import DeleteOutline from '@mui/icons-material/DeleteOutlined';

interface Props {
  currentView: string;
  onNavigate: (view: string) => void;
  onOpenFilter?: () => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onOpenStorageConfig?: () => void;
  onExportBackup?: () => void;
  onImportBackup?: () => void;
  exploreTab?: 'sources' | 'extensions' | 'migrate';
  onExploreTabChange?: (tab: 'sources' | 'extensions' | 'migrate') => void;
  onClearHistory?: () => void;
  onRefreshUpdates?: () => void;
}

export function YomiriMobileTopBar({
  currentView,
  onNavigate,
  onOpenFilter,
  searchQuery = '',
  onSearchChange,
  onOpenStorageConfig,
  onExportBackup,
  onImportBackup,
  exploreTab = 'sources',
  onExploreTabChange,
  onClearHistory,
  onRefreshUpdates,
}: Props) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSearchOpen(false);
    setAnchor(null);
    onSearchChange?.('');
  }, [currentView]);

  if (currentView === 'more') return null;

  const explore = ['sources', 'explore'].includes(currentView);
  const title =
    ({
      library: 'Biblioteca',
      downloads: 'Descargas',
      updates: 'Novedades',
      history: 'Historial',
      sources: 'Fuentes',
      explore: 'Manga',
      appearance: 'Apariencia',
      about: 'Acerca de Yomiri',
    } as Record<string, string>)[currentView] || 'Yomiri';

  const closeSearch = () => {
    setSearchOpen(false);
    onSearchChange?.('');
  };

  const menuAction = (action?: () => void) => {
    setAnchor(null);
    action?.();
  };

  return (
    <Box
      component="header"
      sx={{
        display: { xs: 'block', md: 'none' },
        bgcolor: 'rgba(15,20,28,0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        pt: 'env(safe-area-inset-top)',
        flexShrink: 0,
        zIndex: 1100,
        position: 'sticky',
        top: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 48,
          px: 1.5,
          gap: 1,
        }}
      >
        {searchOpen ? (
          <>
            <IconButton aria-label="Cerrar búsqueda" size="small" onClick={closeSearch} sx={{ color: 'var(--y-onSurface)' }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <InputBase
              autoFocus
              placeholder={`Buscar en ${title}…`}
              inputProps={{ 'aria-label': 'Buscar en ' + title }}
              value={searchQuery}
              onChange={event => onSearchChange?.(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Escape') closeSearch();
              }}
              sx={{
                flex: 1,
                fontSize: 14,
                bgcolor: 'rgba(255,255,255,0.06)',
                borderRadius: '4px',
                px: 1.2,
                py: 0.3,
                color: 'var(--y-onSurface)',
              }}
            />
            {searchQuery && (
              <IconButton aria-label="Limpiar búsqueda" size="small" onClick={() => onSearchChange?.('')} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
                <Close fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <>
            {['about', 'appearance'].includes(currentView) ? (
              <IconButton aria-label="Volver" size="small" onClick={() => onNavigate('more')} sx={{ color: 'var(--y-onSurface)' }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => onNavigate('home')}>
                <YomiriLogo size="small" markOnly />
              </Box>
            )}

            <Typography
              component="h1"
              sx={{
                flex: 1,
                fontSize: 15,
                fontWeight: 650,
                color: 'var(--y-onSurface)',
                letterSpacing: '-0.01em',
                ml: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>

            {['library', 'history', 'sources'].includes(currentView) && (
              <IconButton aria-label="Buscar" size="small" onClick={() => setSearchOpen(true)} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
                <Search fontSize="small" />
              </IconButton>
            )}
            {currentView === 'library' && (
              <IconButton aria-label="Filtrar" size="small" onClick={onOpenFilter} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
                <FilterList fontSize="small" />
              </IconButton>
            )}
            {currentView === 'updates' && (
              <IconButton aria-label="Actualizar" size="small" onClick={onRefreshUpdates} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
                <Refresh fontSize="small" />
              </IconButton>
            )}
            {currentView === 'history' && onClearHistory && (
              <IconButton aria-label="Borrar historial" size="small" onClick={onClearHistory} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
            {currentView !== 'about' && (
              <IconButton aria-label="Más opciones" size="small" onClick={event => setAnchor(event.currentTarget)} sx={{ color: 'var(--y-onSurfaceVariant)' }}>
                <MoreVert fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </Box>

      {explore && null}

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '4px',
              border: '1px solid var(--y-outlineVariant)',
              bgcolor: 'var(--y-containerLow)',
              backgroundImage: 'none',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        {currentView === 'library' && [
          <MenuItem key="storage" onClick={() => menuAction(onOpenStorageConfig)} sx={{ fontSize: 13, py: 1 }}>
            Datos y almacenamiento
          </MenuItem>,
          <MenuItem key="export" onClick={() => menuAction(onExportBackup)} sx={{ fontSize: 13, py: 1 }}>
            Crear copia de seguridad
          </MenuItem>,
          <MenuItem key="import" onClick={() => menuAction(onImportBackup)} sx={{ fontSize: 13, py: 1 }}>
            Restaurar copia de seguridad
          </MenuItem>,
        ]}
        <MenuItem onClick={() => menuAction(() => onNavigate('about'))} sx={{ fontSize: 13, py: 1 }}>
          Acerca de Yomiri
        </MenuItem>
      </Menu>
    </Box>
  );
}
